import { registry } from '@cards/cards';
import type { PixelGlyph } from '@cards/cards';
import type { CardDefId, CardType } from '@cards/engine';
import type { CSSProperties, ReactElement } from 'react';
import { AuthoredGlyph } from './AuthoredGlyph.js';
import { CardGlyph } from './CardGlyph.js';
import { PixelPattern, type PixelPatternDef } from './PixelPattern.js';
import { PixelIcon } from './PixelIcon.js';
import { useCardTilt } from './useCardTilt.js';
import { cardDescription, cardKeywordHelp, cardName, cardTypeLabel } from './locale.js';

export interface CardInfoStatus {
  id: string;
  label: string;
  description: string;
  color: string;
}

export interface CardInfoData {
  defId: CardDefId;
  /** Optional non-card presentation kind used by shared compact renderers. */
  kind?: 'deck-passive';
  /** Display-name override (e.g. the combined cell-state card). */
  name?: string;
  /** Description override; an empty string hides the description entirely. */
  description?: string;
  /** Hide the header glyph (the cell-state card is not a real card). */
  hideGlyph?: boolean;
  /** Explicit pixel art for surfaces whose defId is not in the card catalog (deck passives). */
  glyph?: PixelGlyph;
  /** Header meta-line override; replaces the derived card-type label (e.g. "Пассивка колоды"). */
  metaLabel?: string;
  /** Decorative tiled background pattern behind the card content (deck passives). */
  pattern?: PixelPatternDef;
  /** Hide keyword help (when the state entries already explain everything). */
  hideKeywords?: boolean;
  type?: CardType;
  /** A token (02 §3.2): the token face and ring, and «Токен» in place of the type. */
  token?: boolean;
  cost?: number;
  /** Present only for card definitions that expose combat stats. */
  attack?: number;
  health?: number;
  maxHealth?: number;
  keywords?: readonly string[];
  statuses?: readonly CardInfoStatus[];
}

export interface CardInfoCardProps {
  data: CardInfoData;
  density: 'compact' | 'comfortable';
  keywordDisplay?: 'expanded' | 'collapsed';
  testId: string;
  titleId?: string;
  descriptionId?: string;
}

/** Canonical projection for definition-only card surfaces such as deck building. */
export function cardInfoDataFromDefinition(defId: CardDefId): CardInfoData | undefined {
  const definition = registry.get(defId);
  if (definition === undefined) return undefined;
  return {
    defId,
    type: definition.type,
    ...(definition.cost === undefined ? {} : { cost: definition.cost }),
    ...(definition.baseStats === undefined ? {} : {
      attack: definition.baseStats.attack,
      health: definition.baseStats.health,
      maxHealth: definition.baseStats.health,
    }),
    keywords: definition.keywords ?? [],
  };
}

function resourceLabel(data: CardInfoData): string | undefined {
  return data.cost === undefined ? undefined : `Энергия: ${data.cost}`;
}

/** The art of the large card: a 20×20 glyph six times over (01 §10). */
const CARD_ART_SIZE = 120;

/** Shared full card information renderer used by hover, dialog and embedded surfaces. */
export function CardInfoCard({
  data,
  density,
  keywordDisplay = 'expanded',
  testId,
  titleId,
  descriptionId,
}: CardInfoCardProps): ReactElement {
  const description = data.description ?? cardDescription(data.defId);
  const keywords = data.hideKeywords === true ? [] : cardKeywordHelp(data.defId, data.keywords ?? []);
  const resources = resourceLabel(data);
  const compact = density === 'compact';
  const hasStats = data.attack !== undefined && data.health !== undefined;
  const tilt = useCardTilt();
  return (
    <div
      className={`cards-card-detail-panel cards-card-detail-${density} cards-tilt`}
      data-testid={testId}
      data-card-type={data.type}
      data-token={data.token === true ? 'true' : undefined}
      {...tilt}
    >
      <span className="cards-tilt-glare" aria-hidden="true" />
      {data.pattern === undefined ? null : (
        <PixelPattern
          pattern={data.pattern}
          id={`${testId}-pattern`}
          testId={`${testId}-pattern`}
          className="cards-card-detail-pattern"
        />
      )}
      <div className="cards-card-detail-header">
        {/* The cost coin of the large card (02 §3.1 L); the meta line keeps the
           cost in words for assistive technology. */}
        {data.cost === undefined ? null : (
          <span className="cards-card-detail-coin" aria-hidden="true">{data.cost}</span>
        )}
        {data.hideGlyph === true ? null : data.glyph === undefined ? (
          <CardGlyph defId={data.defId} size={CARD_ART_SIZE} />
        ) : (
          <AuthoredGlyph
            glyph={data.glyph}
            testId={`card-glyph-${data.defId}`}
            visualId={data.defId}
            size={CARD_ART_SIZE}
          />
        )}
        <div className="cards-card-detail-heading">
          <div className="cards-card-detail-meta" data-card-region="meta">
            {data.metaLabel ?? (data.token === true ? 'Токен' : cardTypeLabel(data.type))}
            {resources === undefined ? null : (
              <span className="cards-card-detail-meta-resource">{` · ${resources}`}</span>
            )}
          </div>
          <div id={titleId} className="cards-card-detail-title" data-card-region="title">
            {data.name ?? cardName(data.defId)}
          </div>
        </div>
      </div>
      {hasStats ? (
        <div className="cards-card-detail-stats" data-card-region="stats">
          <span aria-label={`Атака: ${data.attack}`}><PixelIcon name="attack" /> {data.attack}</span>
          <span aria-label={`Здоровье: ${data.health}${data.maxHealth === undefined ? '' : ` из ${data.maxHealth}`}`}>
            <PixelIcon name="health" /> {data.health}{data.maxHealth === undefined ? '' : `/${data.maxHealth}`}
          </span>
        </div>
      ) : null}
      <div className="cards-card-detail-scroll-content">
        {description === '' ? null : (
          <p id={descriptionId} className="cards-card-detail-description" data-card-region="description">
            {description ?? 'У этой карты нет дополнительного описания.'}
          </p>
        )}
        {(data.statuses?.length ?? 0) > 0 ? (
          <section
            className="cards-card-detail-section cards-card-detail-statuses"
            data-testid={`${testId}-statuses`}
            aria-label="Текущее состояние карты"
          >
            <div className="cards-card-detail-section-label">ТЕКУЩЕЕ СОСТОЯНИЕ</div>
            {data.statuses?.map((status) => (
              <div
                key={status.id}
                className="cards-card-detail-status"
                style={{ '--cards-card-status-color': status.color } as CSSProperties}
              >
                <strong>{status.label}</strong>
                <span>{status.description}</span>
              </div>
            ))}
          </section>
        ) : null}
        {keywords.length > 0 && keywordDisplay === 'collapsed' ? (
          <section
            className="cards-card-detail-section cards-card-detail-keywords cards-card-detail-keywords-collapsed"
            data-testid={`${testId}-keywords`}
            aria-label="Описания ключевых слов"
          >
            <div className="cards-card-detail-section-label">КЛЮЧЕВЫЕ СЛОВА</div>
            {keywords.map((keyword) => (
              <details
                key={keyword.id}
                data-testid={`${testId}-keyword-${keyword.id}`}
                className="cards-card-detail-keyword"
              >
                <summary>{keyword.label}</summary>
                <p>{keyword.description}</p>
              </details>
            ))}
          </section>
        ) : keywords.length > 0 ? (
          <section
            className="cards-card-detail-section cards-card-detail-keywords"
            data-testid={`${testId}-keywords`}
            aria-label="Описания ключевых слов"
          >
            <div className="cards-card-detail-section-label">КЛЮЧЕВЫЕ СЛОВА</div>
            {keywords.map((keyword) => compact ? (
              <div
                key={keyword.id}
                data-testid={`${testId}-keyword-${keyword.id}`}
                className="cards-card-detail-keyword cards-card-detail-keyword-static"
              >
                <strong>{keyword.label}</strong>
                <span>{keyword.description}</span>
              </div>
            ) : (
              <details
                key={keyword.id}
                data-testid={`${testId}-keyword-${keyword.id}`}
                className="cards-card-detail-keyword"
              >
                <summary>{keyword.label}</summary>
                <p>{keyword.description}</p>
              </details>
            ))}
          </section>
        ) : null}
      </div>
    </div>
  );
}
